import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useGetDashboardRequiredAlertsList } from '@nxsys/hooks/api/dashboard/useGetDashboardRequiredAlertsList';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import type { RequiredAlertsListType } from '@nxsys/store/slices/dashboard/types';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { getRequiredAlertsListColumns } from '../../../analytics/modals/RequiredAlertsList/columns';
import RequiredAlertEditDrawer from './RequiredAlertEditDrawer';
import { isEditableAlert } from './editAlertTargets';

type RequiredAlertsListModalProps = {
  isOpen: boolean;
  title: string;
  toggle: () => void;
  type: RequiredAlertsListType;
  /** NX-4682: the same payment-model scope the dashboard card uses, so the
   *  popup count matches the card. */
  paymentModels?: string[];
  /** Called after the quick-edit drawer closes, so the host panel can refresh
   *  its alert badge counts (NX-4682). */
  onChanged?: () => void;
};

const RequiredAlertsListModal: React.FC<RequiredAlertsListModalProps> = ({
  title,
  isOpen,
  toggle,
  type,
  paymentModels,
  onChanged,
}) => {
  const { messages } = useTypedIntl();
  const navigate = useNavigateWithQuery();

  const {
    loading,
    data,
    fetchRequiredAlertsList,
    queryParams,
    searchTerm,
    setSearchTerm,
  } = useGetDashboardRequiredAlertsList({ type, paymentModels });

  const [editWorkerId, setEditWorkerId] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const editable = isEditableAlert(type);

  const listData = data?.data ?? [];
  const onView = (workerId: string) => {
    toggle();
    navigate(`/employees-subcontractors/${workerId}`);
  };
  const onEdit = (workerId: string) => {
    setEditWorkerId(workerId);
    setEditOpen(true);
  };
  const closeEdit = () => {
    setEditOpen(false);
    setEditWorkerId(null);
    // A fixed worker should drop off the list; also refresh the panel badge.
    fetchRequiredAlertsList({
      page: queryParams.page ?? 0,
      size: queryParams.size ?? 20,
      search: queryParams.search,
    });
    onChanged?.();
  };
  const columns = getRequiredAlertsListColumns(
    type,
    editable ? { onEdit } : { onView },
  );
  const scrollX = columns.reduce(
    (acc, col) => acc + (typeof col.width === 'number' ? col.width : 150),
    80,
  );

  useEffect(() => {
    if (isOpen) {
      fetchRequiredAlertsList({
        page: 0,
        size: 20,
      });
    } else {
      setSearchTerm('');
    }
  }, [isOpen, type]);

  const handleTableChange = (page: number, size: number) => {
    const isPageSizeChanged = size !== queryParams.size;
    const shouldResetPage =
      isPageSizeChanged &&
      queryParams.page !== undefined &&
      queryParams.page > 0;

    fetchRequiredAlertsList({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size,
    });
  };

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      fetchRequiredAlertsList({
        ...queryParams,
        page: 0,
        size: queryParams.size ?? 20,
        search: search || undefined,
      });
    }, 500),
    [queryParams, type],
  );

  const onChangeSearchTerm = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    debouncedFetchData(e.target.value);
  };

  return (
    <>
      <AppModalContainer
        title={title}
        width={1200}
        isOpen={isOpen}
        toggle={toggle}
        hiddenFooter
        className="max-sm:w-[100vw]"
        minHeight="0"
        noPaddingBody
      >
        <div className="flex justify-between flex-wrap w-full gap-4 px-5 py-4">
          <AppInputSearch
            placeholder={messages['common.search']}
            value={searchTerm}
            onChange={onChangeSearchTerm}
            className="!w-full md:!w-[280px]"
          />
        </div>
        <AppTableContainer
          rowKey="id"
          data={listData}
          loading={loading}
          page={queryParams.page !== undefined ? queryParams.page : 0}
          total={data?.totalDocs ?? 0}
          pageSize={queryParams.size ?? 20}
          scroll={{ x: scrollX, y: 600 }}
          columns={columns}
          onChangePagination={handleTableChange}
        />
      </AppModalContainer>
      <RequiredAlertEditDrawer
        isOpen={editOpen}
        workerId={editWorkerId}
        type={type}
        title={title}
        toggle={closeEdit}
      />
    </>
  );
};

export default RequiredAlertsListModal;
